import React, { useEffect, useState } from 'react';
import { useNavigate as useReactRouterNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import i18n, {
  SUPPORTED_LANGUAGES,
  LANG_STORAGE_KEY,
  type SupportedLanguage,
} from '../i18n';
import { localizedPath, readLangFromPath } from '../i18n/router';

const DISMISS_KEY = 'betraveling_lang_dismissed';

/**
 * Détecte la langue préférée du navigateur si elle fait partie des langues supportées.
 */
function detectBrowserLang(): SupportedLanguage | null {
  if (typeof navigator === 'undefined') return null;
  const nav = (navigator.languages && navigator.languages[0]) || navigator.language || '';
  const code = nav.toLowerCase().split('-')[0];
  if ((SUPPORTED_LANGUAGES as readonly string[]).includes(code)) {
    return code as SupportedLanguage;
  }
  return null;
}

/**
 * Bandeau qui suggère à l'utilisateur de basculer dans sa langue préférée
 * si celle-ci diffère de la langue actuelle de l'app. Réplique du composant
 * `bt-lang-suggest` du site marketing.
 */
const BTLangSuggest: React.FC = () => {
  const { t } = useTranslation('i18n');
  const navigate = useReactRouterNavigate();
  const location = useLocation();
  const [target, setTarget] = useState<SupportedLanguage | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Ne pas afficher si déjà refusé ou si l'utilisateur a déjà choisi une langue.
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
      if (localStorage.getItem(LANG_STORAGE_KEY)) return;
    } catch {
      /* ignore */
    }

    const browserLang = detectBrowserLang();
    if (!browserLang) return;

    const current = readLangFromPath(location.pathname) || (i18n.language as SupportedLanguage) || 'fr';
    if (browserLang === current) return;

    // Pas de suggestion vers FR (langue par défaut)
    if (browserLang === 'fr') return;

    setTarget(browserLang);
    // Délai léger avant apparition pour ne pas s'afficher au tout premier paint
    const id = window.setTimeout(() => setVisible(true), 400);
    return () => window.clearTimeout(id);
  }, [location.pathname]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const accept = () => {
    if (!target) return;
    try {
      localStorage.setItem(LANG_STORAGE_KEY, target);
    } catch {
      /* ignore */
    }
    void i18n.changeLanguage(target);
    const url = localizedPath(location.pathname, target) + location.search + location.hash;
    navigate(url);
    setVisible(false);
  };

  if (!target) return null;

  // Récupère les textes du bandeau dans la langue cible (i18n.t avec lng override).
  const text = i18n.getFixedT(target, 'i18n')('suggest.text');
  const btn = i18n.getFixedT(target, 'i18n')('suggest.btn');
  const dismissLabel = i18n.getFixedT(target, 'i18n')('suggest.dismiss');
  const icon = i18n.getFixedT(target, 'i18n')('suggest.icon');

  // Le bandeau est fixe sous la BTLangBar pour ne pas la masquer.
  return (
    <Box
      role="region"
      aria-label={text}
      sx={{
        position: 'fixed',
        top: 48,
        left: 0,
        right: 0,
        zIndex: 1299,
        background: 'linear-gradient(180deg, #FFF8EC 0%, #F5C770 100%)',
        borderBottom: '2px solid #D89D2F',
        boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          py: 1.5,
          px: 2.25,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: 'center',
        }}
      >
        <Box component="span" sx={{ fontSize: 22 }}>
          {icon}
        </Box>
        <Box component="span" sx={{ color: '#5a3f0e', fontSize: 15, fontWeight: 500 }}>
          {text}
        </Box>
        <Box
          component="button"
          onClick={accept}
          sx={{
            background: '#D89D2F',
            color: '#fff',
            border: 'none',
            py: 1,
            px: 2.25,
            borderRadius: '6px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'background 0.18s ease',
            '&:hover': { background: '#B5811F' },
          }}
        >
          {btn}
        </Box>
        <Box
          component="button"
          onClick={dismiss}
          sx={{
            background: 'transparent',
            color: '#7a5a1e',
            border: '1px solid rgba(122,90,30,0.4)',
            py: 0.875,
            px: 1.75,
            borderRadius: '6px',
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'background 0.18s ease',
            '&:hover': { background: 'rgba(255,255,255,0.5)' },
          }}
        >
          {dismissLabel}
        </Box>
      </Box>
      <IconButton
        aria-label="Close"
        onClick={dismiss}
        size="small"
        sx={{
          position: 'absolute',
          top: 6,
          right: 10,
          color: '#7a5a1e',
          '&:hover': { color: '#3a2a08' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default BTLangSuggest;
